import React, { useState } from "react";
import PropTypes from "prop-types";

import { PlayPauseButton } from "./buttons/PlayPauseButton";
import { SwipeUp } from "./SwipeUp";
import { VideoScrubber } from "./VideoScrubber";
import Volume from "./Volume";
import Headline from "./Headline";
import LoadingState from "./LoadingState";
import { hasBurnedHeadline } from "../orca/utils";
import {
  BottomControls,
  BottomControlsWrapper,
  ControlsWrapper,
  PlayPauseWrapper
} from "./index.stitches";
import { useVideoDataContext } from "../orca/contexts/VideoDataContext";
import { useAutoplayContext } from "../orca/contexts/AutoplayContext";
import { useStickyVideoContext } from "../orca/contexts/StickyVideoContext";
import {
  getControlsContainerHeight,
  getGradient,
  getIsMiniPlayer
} from "./utils";
import { ControlsContainer } from "./ControlsContainer";
import { useOrcaCommonContext } from "../orca/contexts/OrcaCommonContext";
import { AdState } from "../globals";
import { useOrcaAdsContext } from "../orca/contexts/OrcaAdsContext";

export const CommonControls = ({
  config,
  isVertical,
  carouselEnd,
  TopControls = () => {},
  LeftButtons = () => {},
  closeCarousel,
  hasSubtitles,
  isLive
}) => {
  const { videoData } = useVideoDataContext();
  const { autoplayState } = useAutoplayContext();
  const { isOpenSticky } = useStickyVideoContext();
  const { adState } = useOrcaAdsContext();
  const {
    initDuration,
    playerSize,
    playerState,
    isFullscreen,
    handleVolumeChange,
    toggleMute,
    onPlayButton
  } = useOrcaCommonContext();

  const { isCarousel, debug } = config;

  const [isVisible, setIsVisible] = useState(true);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [selected, setSelected] = useState(false);
  const [persistentProgress, setPersistentProgress] = useState(0);

  const isMiniPlayer = getIsMiniPlayer(playerSize, isFullscreen, isVertical);
  const controlsContainerHeight = getControlsContainerHeight();

  const showScrubber = !isMiniPlayer && !isLive && !autoplayState.isLooping;

  const burnedHeadline = hasBurnedHeadline(videoData);
  const showHeadline = isCarousel && !burnedHeadline;

  const basicHeadline = videoData?.headlines.basic || "";

  const togglePlay = () => {
    if (adState !== AdState.PLAYING) onPlayButton();
  };

  return (
    <ControlsContainer
      data-testid="orca-controls"
      tabIndex={0}
      aria-label="Video controls"
      togglePlay={togglePlay}
      controlsContainerHeight={controlsContainerHeight}
      setIsVisible={setIsVisible}
      selected={selected}
      debug={debug}
    >
      <LoadingState />

      <ControlsWrapper
        isVisible={isVisible}
        css={{
          ...getGradient(autoplayState.isLooping, isOpenSticky, isMiniPlayer)
        }}
      >
        <TopControls {...{ config, isVertical, closeCarousel, setSelected }} />

        <BottomControlsWrapper
          isMiniPlayer={isMiniPlayer}
          isCarousel={isCarousel}
          isVertical={isVertical}
        >
          <BottomControls isLooping={autoplayState.isLooping}>
            {showHeadline && (
              <Headline headline={basicHeadline} isVertical={isVertical} />
            )}
            <LeftButtons
              isVertical={isVertical}
              hasSubtitles={hasSubtitles}
              isLive={isLive}
            />
            <PlayPauseWrapper isLooping={autoplayState.isLooping}>
              <PlayPauseButton
                isPlaying={playerState.isPlaying}
                isMiniPlayer={isMiniPlayer}
                buttonStyles={{
                  margin: "-12px -12px 0 -12px",
                  padding: "$075"
                }}
              />
            </PlayPauseWrapper>
            <Volume
              {...{
                setShowVolumeSlider,
                isVertical,
                isMiniPlayer,
                showVolumeSlider,
                playerState,
                handleVolumeChange,
                toggleMute
              }}
              buttonStyles={{
                margin: "-12px -12px 0 -12px",
                padding: "$075"
              }}
            />
          </BottomControls>
          {showScrubber && (
            <VideoScrubber
              setSelected={setSelected}
              initDuration={initDuration}
              persistentProgress={persistentProgress}
              setPersistentProgress={setPersistentProgress}
            />
          )}
        </BottomControlsWrapper>

        {isCarousel && !carouselEnd && <SwipeUp />}
      </ControlsWrapper>
    </ControlsContainer>
  );
};

CommonControls.propTypes = {
  config: PropTypes.object,
  isVertical: PropTypes.bool,
  carouselEnd: PropTypes.bool,
  TopControls: PropTypes.elementType,
  LeftButtons: PropTypes.elementType,
  isLive: PropTypes.bool,
  closeCarousel: PropTypes.func,
  hasSubtitles: PropTypes.bool
};
